import React, { useEffect, useState, useCallback } from "react";
import * as func from "../../api/apiHelper";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faImage, faFont, faFire, faEye, faHeart, faRefresh,
  faSpinner, faBolt, faUsers, faArrowTrendUp, faLightbulb,
  faShareNodes, faCircleInfo, faTriangleExclamation,
} from "@fortawesome/free-solid-svg-icons";

// ─── Utilities ────────────────────────────────────────────────────────────────

const fmt = (n) => (n ?? 0).toLocaleString("en-IN");
const pct = (a, b) => (b > 0 ? `${Math.round((a / b) * 100)}%` : "0%");
const fmtViews = (n) => (n >= 1000 ? `${(n / 1000).toFixed(1)}k` : fmt(n));

const PACK_META = {
  free:          { label: "Free",         color: "#94A3B8", bg: "bg-slate-100",   text: "text-slate-600"   },
  spark_bundle:  { label: "Spark Bundle", color: "#F59E0B", bg: "bg-amber-50",    text: "text-amber-600"   },
  premium:       { label: "Premium",      color: "#6366F1", bg: "bg-indigo-50",   text: "text-indigo-600"  },
  elite:         { label: "Elite",        color: "#C9A84C", bg: "bg-yellow-50",   text: "text-yellow-700"  },
  annual:        { label: "Annual",       color: "#10B981", bg: "bg-emerald-50",  text: "text-emerald-700" },
  single_unlock: { label: "Unlock",       color: "#EC4899", bg: "bg-pink-50",     text: "text-pink-600"    },
};

const TYPE_META = {
  text:          { label: "Text",          icon: faFont,       color: "#6366F1", bg: "bg-indigo-50", text: "text-indigo-600" },
  photo:         { label: "Photo",         icon: faImage,      color: "#EC4899", bg: "bg-pink-50",   text: "text-pink-600"   },
  profile_share: { label: "Profile Share", icon: faShareNodes, color: "#10B981", bg: "bg-emerald-50",text: "text-emerald-600" },
};

const STREAK_LABELS = { 1: "1 day", 2: "2–3 days", 4: "4–7 days", 8: "8–14 days", 15: "15–29 days", "30+": "30+ days" };

const truncate = (str, n) => str && str.length > n ? str.slice(0, n) + "…" : str ?? "—";

const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("en-IN", { day: "2-digit", month: "short" }); }
  catch { return iso; }
};

// ─── KPI Card ────────────────────────────────────────────────────────────────

const KpiCard = ({ label, value, sub, icon, iconBg, iconColor, accent, alert }) => (
  <div className={`bg-white rounded-2xl border shadow-sm p-5 ${alert ? "border-orange-200" : "border-slate-100"}`}>
    <div className="flex items-start justify-between mb-3">
      <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${iconBg}`}>
        <FontAwesomeIcon icon={icon} className={`text-sm ${iconColor}`} />
      </div>
      {alert && (
        <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-orange-100 text-orange-600">
          ⚠ Alert
        </span>
      )}
    </div>
    <p className={`text-2xl font-bold mb-0.5 ${accent ?? "text-slate-800"}`}>{value}</p>
    <p className="text-xs font-medium text-slate-500">{label}</p>
    {sub && <p className="text-[10px] text-slate-400 mt-1">{sub}</p>}
  </div>
);

// ─── Dual Bar+Line Chart ──────────────────────────────────────────────────────

const DualChart = ({ data }) => {
  if (!data?.length) return <Empty />;
  const maxPosts = Math.max(...data.map((d) => d.posts ?? 0), 1);
  const maxViews = Math.max(...data.map((d) => d.views ?? 0), 1);
  const W = 560, H = 120, BOTTOM = 24, BAR_W = Math.floor(W / data.length) - 6;

  const points = data.map((d, i) => {
    const x = i * (W / data.length) + (W / data.length) / 2;
    const y = H - ((d.views ?? 0) / maxViews) * H;
    return `${x},${y}`;
  }).join(" ");

  return (
    <div className="overflow-x-auto">
      <svg viewBox={`0 0 ${W} ${H + BOTTOM}`} className="w-full min-w-[340px]" style={{ height: 160 }}>
        {/* Gridlines */}
        {[0.25, 0.5, 0.75, 1].map((f) => (
          <line key={f} x1={0} y1={H * (1 - f)} x2={W} y2={H * (1 - f)}
            stroke="#F1F5F9" strokeWidth={1} />
        ))}
        {/* Bars (posts) */}
        {data.map((d, i) => {
          const barH = Math.max(((d.posts ?? 0) / maxPosts) * H, d.posts > 0 ? 4 : 0);
          const x = i * (W / data.length) + (W / data.length) / 2 - BAR_W / 2;
          return (
            <rect key={i} x={x} y={H - barH} width={BAR_W} height={barH}
              rx={3} fill="url(#barGrad)" opacity={0.85}>
              <title>{d._id}: {d.posts} posts, {d.views} views</title>
            </rect>
          );
        })}
        {/* Line (views) */}
        {data.length > 1 && (
          <polyline points={points} fill="none" stroke="#EC4899" strokeWidth={2}
            strokeLinejoin="round" strokeLinecap="round" />
        )}
        {/* Dots on line */}
        {data.map((d, i) => {
          const x = i * (W / data.length) + (W / data.length) / 2;
          const y = H - ((d.views ?? 0) / maxViews) * H;
          return <circle key={i} cx={x} cy={y} r={3} fill="#EC4899" stroke="white" strokeWidth={1.5} />;
        })}
        {/* X-axis labels */}
        {data.map((d, i) => {
          const show = i === 0 || i === data.length - 1 || data.length <= 7;
          return show ? (
            <text key={i} x={i * (W / data.length) + (W / data.length) / 2}
              y={H + 16} textAnchor="middle" fontSize={9} fill="#94A3B8">
              {d._id?.slice(5) ?? ""}
            </text>
          ) : null;
        })}
        {/* Gradient def */}
        <defs>
          <linearGradient id="barGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#818CF8" />
            <stop offset="100%" stopColor="#6366F1" />
          </linearGradient>
        </defs>
      </svg>
      {/* Legend */}
      <div className="flex gap-5 mt-2">
        <div className="flex items-center gap-1.5 text-[10px] text-slate-500">
          <div className="w-3 h-3 rounded-sm bg-indigo-400" /> Posts (bars)
        </div>
        <div className="flex items-center gap-1.5 text-[10px] text-slate-500">
          <div className="w-6 h-0.5 bg-pink-500 rounded-full" /> Views (line)
        </div>
      </div>
    </div>
  );
};

// ─── Peak Hours Heatmap ───────────────────────────────────────────────────────

const PeakHoursHeatmap = ({ data }) => {
  const counts = new Array(24).fill(0);
  (data ?? []).forEach((d) => { counts[d._id] = d.count ?? 0; });
  const maxVal = Math.max(...counts, 1);
  const periods = [
    { label: "Early Morning", range: "12–5am", hours: [0,1,2,3,4,5] },
    { label: "Morning",       range: "6–11am", hours: [6,7,8,9,10,11] },
    { label: "Afternoon",     range: "12–5pm", hours: [12,13,14,15,16,17] },
    { label: "Evening/Night", range: "6–11pm", hours: [18,19,20,21,22,23] },
  ];
  const peak = counts.indexOf(Math.max(...counts));

  return (
    <div>
      {periods.map((p) => (
        <div key={p.label} className="mb-3">
          <p className="text-[9px] text-slate-400 mb-1 font-medium">{p.label} <span className="font-normal">({p.range} IST)</span></p>
          <div className="flex gap-1">
            {p.hours.map((h) => {
              const intensity = counts[h] / maxVal;
              const isPeak = h === peak;
              return (
                <div key={h} className="flex-1 group relative">
                  <div
                    className={`h-8 rounded transition-transform hover:scale-110 cursor-default ${isPeak ? "ring-1 ring-indigo-400" : ""}`}
                    style={{
                      background: counts[h] === 0
                        ? "#F8FAFC"
                        : `rgba(99,102,241,${0.12 + intensity * 0.88})`,
                    }}
                    title={`${h}:00–${h + 1}:00 IST — ${counts[h]} posts`}
                  />
                  {/* Tooltip on hover */}
                  <div className="hidden group-hover:block absolute bottom-10 left-1/2 -translate-x-1/2 bg-slate-800 text-white text-[9px] px-2 py-1 rounded whitespace-nowrap z-10 pointer-events-none">
                    {h}h: {counts[h]}
                  </div>
                  {(h % 3 === 0) && (
                    <p className="text-center text-[8px] text-slate-300 mt-0.5">{h}</p>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ))}
      {maxVal > 0 && (
        <p className="text-[10px] text-indigo-500 font-medium mt-2">
          📍 Peak posting hour: {peak}:00–{peak + 1}:00 IST ({counts[peak]} posts)
        </p>
      )}
    </div>
  );
};

// ─── Streak Distribution ──────────────────────────────────────────────────────

const StreakDistribution = ({ data, streakAtRisk }) => {
  const total = (data ?? []).reduce((s, d) => s + (d.count ?? 0), 0) || 1;
  const maxCount = Math.max(...(data ?? []).map((d) => d.count ?? 0), 1);
  const COLORS = ["#94A3B8", "#F59E0B", "#6366F1", "#C9A84C", "#10B981", "#EF4444"];

  return (
    <div>
      {data?.length === 0 && <Empty />}
      <div className="space-y-2.5">
        {(data ?? []).map((d, i) => {
          const barPct = Math.round(((d.count ?? 0) / maxCount) * 100);
          const sharePct = Math.round(((d.count ?? 0) / total) * 100);
          return (
            <div key={d._id ?? i}>
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs text-slate-600 font-medium">
                  {STREAK_LABELS[d._id] ?? `${d._id} days`}
                </span>
                <span className="text-[10px] text-slate-400">{fmt(d.count)} &middot; {sharePct}%</span>
              </div>
              <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                <div className="h-full rounded-full transition-all duration-700"
                  style={{ width: `${barPct}%`, background: COLORS[i % COLORS.length] }} />
              </div>
            </div>
          );
        })}
      </div>
      {streakAtRisk > 0 && (
        <div className="mt-4 flex items-start gap-2 bg-orange-50 border border-orange-200 rounded-xl p-3">
          <FontAwesomeIcon icon={faTriangleExclamation} className="text-orange-500 text-sm mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-xs font-semibold text-orange-700">{streakAtRisk} streaks at risk</p>
            <p className="text-[10px] text-orange-600 leading-relaxed mt-0.5">
              These users haven't posted in 18–22h. They'll break their streak in the next 2–6h. Consider sending a push nudge now.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Engagement Funnel ────────────────────────────────────────────────────────

const EngagementFunnel = ({ totalViews, interestsSent, conversionRate }) => {
  const steps = [
    { label: "Status Views",    value: totalViews,     color: "#6366F1", icon: faEye },
    { label: "Interests Sent",  value: interestsSent,  color: "#EC4899", icon: faHeart },
  ];
  const maxVal = Math.max(...steps.map((s) => s.value), 1);
  return (
    <div className="space-y-3">
      {steps.map((step, i) => {
        const widthPct = Math.round((step.value / maxVal) * 100);
        const convPct = i > 0 ? pct(step.value, steps[i - 1].value) : null;
        return (
          <div key={step.label}>
            <div className="flex items-center justify-between mb-1.5">
              <div className="flex items-center gap-2">
                <FontAwesomeIcon icon={step.icon} style={{ color: step.color }} className="text-xs" />
                <span className="text-xs font-semibold text-slate-700">{step.label}</span>
              </div>
              <div className="flex items-center gap-2">
                {convPct && (
                  <span className="text-[10px] text-slate-400">↳ {convPct} of prior step</span>
                )}
                <span className="text-sm font-bold text-slate-800">{fmtViews(step.value)}</span>
              </div>
            </div>
            <div className="h-7 bg-slate-50 rounded-lg overflow-hidden">
              <div
                className="h-full rounded-lg flex items-center pl-3 text-white text-[10px] font-semibold transition-all duration-1000"
                style={{ width: `${Math.max(widthPct, 4)}%`, background: step.color, opacity: 0.85 }}
              >
                {widthPct > 20 ? fmtViews(step.value) : ""}
              </div>
            </div>
          </div>
        );
      })}
      <div className="flex items-center justify-between mt-1 pt-3 border-t border-slate-100">
        <span className="text-xs text-slate-500 font-medium">View → Interest rate</span>
        <span className="text-sm font-bold text-indigo-600">{conversionRate?.toFixed(1) ?? "0.0"}%</span>
      </div>
    </div>
  );
};

// ─── Content Performance Cards ────────────────────────────────────────────────

const ContentPerformance = ({ typeBreakdown }) => {
  const totalCount = typeBreakdown.reduce((s, t) => s + (t.count ?? 0), 0) || 1;
  const order = ["text", "photo", "profile_share"];
  const sorted = order.map((id) => typeBreakdown.find((t) => t._id === id) ?? { _id: id, count: 0, avgViews: 0, totalViews: 0 });

  return (
    <div className="grid grid-cols-3 gap-3">
      {sorted.map((t) => {
        const meta = TYPE_META[t._id] ?? TYPE_META.text;
        const share = Math.round(((t.count ?? 0) / totalCount) * 100);
        return (
          <div key={t._id} className="bg-white rounded-xl border border-slate-100 shadow-sm p-4">
            <div className={`w-8 h-8 rounded-lg flex items-center justify-center mb-3 ${meta.bg}`}>
              <FontAwesomeIcon icon={meta.icon} className={`text-sm ${meta.text}`} />
            </div>
            <p className="text-xs font-semibold text-slate-500 mb-0.5">{meta.label}</p>
            <p className="text-xl font-bold text-slate-800">{fmt(t.count)}</p>
            <div className="mt-2 space-y-1">
              <div className="flex justify-between text-[10px]">
                <span className="text-slate-400">Share</span>
                <span className="font-semibold text-slate-600">{share}%</span>
              </div>
              <div className="flex justify-between text-[10px]">
                <span className="text-slate-400">Avg views</span>
                <span className="font-semibold text-slate-600">{(t.avgViews ?? 0).toFixed(1)}</span>
              </div>
              <div className="flex justify-between text-[10px]">
                <span className="text-slate-400">Total views</span>
                <span className="font-semibold text-slate-600">{fmtViews(t.totalViews ?? 0)}</span>
              </div>
            </div>
            <div className="mt-2 h-1 bg-slate-100 rounded-full overflow-hidden">
              <div className="h-full rounded-full" style={{ width: `${share}%`, background: meta.color }} />
            </div>
          </div>
        );
      })}
    </div>
  );
};

// ─── Pack Distribution ────────────────────────────────────────────────────────

const PackDistribution = ({ data }) => {
  const total = data.reduce((s, p) => s + (p.count ?? 0), 0) || 1;
  const maxCount = Math.max(...data.map((p) => p.count ?? 0), 1);
  return (
    <div className="space-y-3">
      {data.length === 0 && <Empty />}
      {data.map((p) => {
        const meta = PACK_META[p._id] ?? PACK_META.free;
        const barPct = Math.round(((p.count ?? 0) / maxCount) * 100);
        const sharePct = Math.round(((p.count ?? 0) / total) * 100);
        return (
          <div key={p._id} className="flex items-center gap-3">
            <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full w-24 text-center flex-shrink-0 ${meta.bg} ${meta.text}`}>
              {meta.label}
            </span>
            <div className="flex-1">
              <div className="h-2.5 bg-slate-100 rounded-full overflow-hidden">
                <div className="h-full rounded-full transition-all duration-700"
                  style={{ width: `${barPct}%`, background: meta.color }} />
              </div>
            </div>
            <div className="w-16 text-right flex-shrink-0">
              <span className="text-xs font-bold text-slate-700">{fmt(p.count)}</span>
              <span className="text-[10px] text-slate-400 ml-1">{sharePct}%</span>
            </div>
          </div>
        );
      })}
    </div>
  );
};

// ─── Top Profiles Table ───────────────────────────────────────────────────────

const TopProfilesTable = ({ rows, mode }) => {
  if (!rows?.length) return <Empty />;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs">
        <thead>
          <tr className="text-slate-400 border-b border-slate-100">
            <th className="text-left py-2 font-medium w-6">#</th>
            <th className="text-left py-2 font-medium">Name</th>
            {mode === "views" && <th className="text-left py-2 font-medium">Status</th>}
            <th className="text-left py-2 font-medium">Type</th>
            <th className="text-right py-2 font-medium">Views</th>
            <th className="text-right py-2 font-medium">Streak</th>
            <th className="text-right py-2 font-medium">Posted</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-50">
          {rows.map((r, i) => {
            const typeMeta = TYPE_META[r.statusType] ?? TYPE_META.text;
            return (
              <tr key={r._id ?? i} className="hover:bg-slate-50 transition-colors">
                <td className="py-2.5 text-slate-300 font-semibold">{i + 1}</td>
                <td className="py-2.5 font-semibold text-slate-700 pr-3 whitespace-nowrap">
                  {r.firstName || "—"}
                </td>
                {mode === "views" && (
                  <td className="py-2.5 text-slate-500 max-w-[160px]">
                    <span title={r.statusUpdate ?? ""}>{truncate(r.statusUpdate, 35)}</span>
                  </td>
                )}
                <td className="py-2.5 pr-2">
                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${typeMeta.bg} ${typeMeta.text}`}>
                    {r.statusType === "photo" ? "📷" : r.statusType === "profile_share" ? "🔗" : "📝"} {r.statusType ?? "—"}
                  </span>
                </td>
                <td className="py-2.5 text-right font-bold text-violet-600">{fmt(r.statusViewCount ?? 0)}</td>
                <td className="py-2.5 text-right font-semibold whitespace-nowrap">
                  {(r.statusStreak ?? 0) > 0
                    ? <span className="text-orange-500">🔥 {r.statusStreak}</span>
                    : <span className="text-slate-300">—</span>}
                </td>
                <td className="py-2.5 text-right text-slate-400 whitespace-nowrap">{fmtDate(r.statusUpdatedAt)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

// ─── Smart Insights ───────────────────────────────────────────────────────────

const SmartInsights = ({ overview, typeBreakdown, streakAtRisk }) => {
  const insights = [];

  const photoType = typeBreakdown?.find((t) => t._id === "photo");
  const textType  = typeBreakdown?.find((t) => t._id === "text");

  if (photoType && textType && (photoType.avgViews ?? 0) > (textType.avgViews ?? 0) * 1.5) {
    insights.push({
      icon: "📷",
      title: "Photos drive 1.5× more views than text",
      body: `Photo statuses average ${(photoType.avgViews ?? 0).toFixed(1)} views vs ${(textType.avgViews ?? 0).toFixed(1)} for text. Nudge free users to upgrade to Spark Bundle for photo access.`,
      color: "bg-pink-50 border-pink-200",
    });
  }

  if (streakAtRisk > 0) {
    insights.push({
      icon: "⏰",
      title: `${streakAtRisk} users about to lose their streak`,
      body: "They haven't posted in 18–22h. Sending a streak-save push notification now could recover engagement and reinforce the habit loop.",
      color: "bg-orange-50 border-orange-200",
    });
  }

  if ((overview?.conversionRate ?? 0) > 5) {
    insights.push({
      icon: "🎯",
      title: `${overview.conversionRate?.toFixed(1)}% view-to-interest conversion`,
      body: "Status views are driving real interest signals. Users who view statuses are highly engaged — consider featuring status-heavy profiles higher in Discover.",
      color: "bg-violet-50 border-violet-200",
    });
  } else if ((overview?.conversionRate ?? 0) < 2 && (overview?.totalViews7d ?? 0) > 10) {
    insights.push({
      icon: "💡",
      title: "Low conversion from views to interest",
      body: `Only ${overview?.conversionRate?.toFixed(1)}% of status views convert to interest sent. Consider A/B testing a stronger CTA in the status viewer.`,
      color: "bg-blue-50 border-blue-200",
    });
  }

  const retainRate = overview?.postedToday > 0
    ? Math.round(((overview?.retainedToday ?? 0) / overview.postedToday) * 100)
    : 0;
  if (retainRate > 0) {
    insights.push({
      icon: "🔄",
      title: `${retainRate}% of today's posters also posted yesterday`,
      body: retainRate > 50
        ? "Strong retention signal — the streak mechanic is working. Keep reinforcing with streak milestone FCM notifications."
        : "Retention is moderate. Consider adding streak leaderboards or milestone rewards to increase repeat posting.",
      color: retainRate > 50 ? "bg-emerald-50 border-emerald-200" : "bg-slate-50 border-slate-200",
    });
  }

  if (insights.length === 0) {
    insights.push({
      icon: "📊",
      title: "Waiting for more data",
      body: "Insights will appear here once users start posting statuses. Encourage first posts with onboarding prompts.",
      color: "bg-slate-50 border-slate-200",
    });
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {insights.map((ins, i) => (
        <div key={i} className={`rounded-2xl border p-4 ${ins.color}`}>
          <div className="flex items-start gap-3">
            <span className="text-2xl leading-none">{ins.icon}</span>
            <div>
              <p className="text-sm font-semibold text-slate-800 mb-1">{ins.title}</p>
              <p className="text-xs text-slate-600 leading-relaxed">{ins.body}</p>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

// ─── Empty state ──────────────────────────────────────────────────────────────

const Empty = () => (
  <div className="flex items-center justify-center py-8 text-xs text-slate-300">No data yet</div>
);

// ─── Section wrapper ─────────────────────────────────────────────────────────

const Section = ({ title, sub, children, action }) => (
  <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5">
    <div className="flex items-start justify-between mb-4">
      <div>
        <h2 className="font-semibold text-slate-800 text-sm">{title}</h2>
        {sub && <p className="text-xs text-slate-400 mt-0.5">{sub}</p>}
      </div>
      {action}
    </div>
    {children}
  </div>
);

// ─── Main Component ───────────────────────────────────────────────────────────

const StatusAnalytics = () => {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const [topTab, setTopTab]   = useState("views");
  const [lastRefresh, setLastRefresh] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await func.get("matchmatrix/admin/status/analytics");
      if (res?.status === 200 && res.data?.success) {
        setData(res.data);
        setLastRefresh(new Date());
      } else {
        setError("Failed to load analytics.");
        toast.error("Failed to load status analytics");
      }
    } catch (err) {
      const msg = typeof err === "string" ? err : "Failed to load status analytics";
      setError(msg);
      toast.error(msg);
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const overview          = data?.overview ?? {};
  const typeBreakdown     = data?.typeBreakdown ?? [];
  const packDistribution  = data?.packDistribution ?? [];
  const topByViews        = data?.topByViews ?? [];
  const topByStreak       = data?.topByStreak ?? [];
  const dailyPostCounts   = data?.dailyPostCounts ?? [];
  const peakHours         = data?.peakHours ?? [];
  const streakDistribution = data?.streakDistribution ?? [];

  return (
    <div className="min-h-screen bg-slate-50">

      {/* ─── Header ──────────────────────────────────────────────────────────── */}
      <div className="bg-gradient-to-r from-slate-900 via-violet-950 to-slate-900 px-6 py-6 border-b border-violet-900/40">
        <div className="max-w-7xl mx-auto flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="w-11 h-11 rounded-2xl bg-violet-600/30 border border-violet-500/40 flex items-center justify-center">
              <FontAwesomeIcon icon={faArrowTrendUp} className="text-violet-300 text-base" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-lg font-bold text-white">Status Analytics</h1>
                <span className="flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-green-500/20 text-green-400 border border-green-500/30">
                  <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />
                  Live
                </span>
              </div>
              <p className="text-violet-300 text-xs mt-0.5">
                Posts · Views · Streaks · Conversions · Retention
                {lastRefresh && (
                  <span className="ml-2 text-violet-400">
                    · Refreshed {lastRefresh.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}
                  </span>
                )}
              </p>
            </div>
          </div>
          <button
            onClick={load}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 bg-white/10 text-white text-xs font-semibold rounded-xl hover:bg-white/20 transition border border-white/20 disabled:opacity-50"
          >
            <FontAwesomeIcon icon={loading ? faSpinner : faRefresh} spin={loading} />
            Refresh
          </button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-6 space-y-5">

        {/* Loading */}
        {loading && (
          <div className="flex justify-center py-24">
            <FontAwesomeIcon icon={faSpinner} spin className="text-4xl text-violet-300" />
          </div>
        )}

        {/* Error */}
        {!loading && error && (
          <div className="bg-red-50 border border-red-100 rounded-2xl p-6 text-center">
            <p className="text-red-500 text-sm font-medium">{error}</p>
            <button onClick={load} className="mt-3 px-4 py-2 bg-red-500 text-white text-xs rounded-lg hover:bg-red-600 transition">
              Try Again
            </button>
          </div>
        )}

        {!loading && !error && data && (
          <>
            {/* ─── KPIs ──────────────────────────────────────────────────────── */}
            <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4">
              <KpiCard label="Active Right Now"    value={fmt(overview.activeNow)}       icon={faBolt}          iconBg="bg-green-50"   iconColor="text-green-500"  sub="Within last 24h" />
              <KpiCard label="Posted Today"        value={fmt(overview.postedToday)}     icon={faFont}          iconBg="bg-blue-50"    iconColor="text-blue-500"   sub={`of ${fmt(overview.uniquePosters7d)} last 7d`} />
              <KpiCard label="Active Streakers"    value={fmt(overview.totalStreakers)}   icon={faFire}          iconBg="bg-orange-50"  iconColor="text-orange-500" sub="Streak > 0 days" />
              <KpiCard label="Retained Today"      value={fmt(overview.retainedToday)}   icon={faUsers}         iconBg="bg-violet-50"  iconColor="text-violet-500" sub="Also posted yesterday" />
              <KpiCard label="Avg Views / Status"  value={(overview.avgViewsPerStatus ?? 0).toFixed(1)} icon={faEye} iconBg="bg-slate-100" iconColor="text-slate-500" sub="7-day window" />
              <KpiCard label="View → Interest %"   value={`${(overview.conversionRate ?? 0).toFixed(1)}%`} icon={faHeart} iconBg="bg-pink-50" iconColor="text-pink-500" sub={`${fmt(overview.storyInterestsSent7d)} interests (7d)`} />
            </div>

            {/* ─── Engagement Funnel + Smart Insights ────────────────────────── */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
              <Section title="Engagement Funnel" sub="Status views → interest conversions (7-day window)">
                <EngagementFunnel
                  totalViews={overview.totalViews7d}
                  interestsSent={overview.storyInterestsSent7d}
                  conversionRate={overview.conversionRate}
                />
              </Section>
              <Section title="Smart Insights" sub="Auto-generated signals from your data"
                action={<FontAwesomeIcon icon={faLightbulb} className="text-amber-400 text-sm" />}>
                <SmartInsights overview={overview} typeBreakdown={typeBreakdown} streakAtRisk={overview.streakAtRisk} />
              </Section>
            </div>

            {/* ─── Content Performance ─────────────────────────────────────────── */}
            <Section title="Content Performance" sub="Engagement by status type — 7-day window">
              <ContentPerformance typeBreakdown={typeBreakdown} />
            </Section>

            {/* ─── 7-Day Chart ─────────────────────────────────────────────────── */}
            <Section
              title="7-Day Post Volume & Views"
              sub="Daily posts (bars) vs total status views (pink line)"
              action={
                dailyPostCounts.length > 0 && (
                  <div className="text-right">
                    <p className="text-[10px] text-slate-400">Peak</p>
                    <p className="text-sm font-bold text-indigo-600">
                      {fmt(Math.max(...dailyPostCounts.map((d) => d.posts ?? 0)))} posts
                    </p>
                  </div>
                )
              }
            >
              <DualChart data={dailyPostCounts} />
            </Section>

            {/* ─── Peak Hours + Streak Health ──────────────────────────────────── */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
              <Section title="Peak Posting Hours" sub="When users post statuses — IST, last 7 days">
                <PeakHoursHeatmap data={peakHours} />
              </Section>
              <Section title="Streak Health" sub="Distribution of active streak lengths">
                <StreakDistribution data={streakDistribution} streakAtRisk={overview.streakAtRisk} />
              </Section>
            </div>

            {/* ─── Pack Distribution ───────────────────────────────────────────── */}
            <Section title="Pack Distribution" sub="Who's posting — by subscription tier (7-day)">
              <PackDistribution data={packDistribution} />
            </Section>

            {/* ─── Top Profiles ────────────────────────────────────────────────── */}
            <Section title="Top Profiles" sub="Most active and highest-visibility status posters">
              {/* Tab switcher */}
              <div className="flex gap-2 mb-5">
                {[
                  { id: "views",  label: "By Views" },
                  { id: "streak", label: "By Streak 🔥" },
                ].map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setTopTab(tab.id)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition ${
                      topTab === tab.id
                        ? "bg-violet-600 text-white"
                        : "bg-slate-100 text-slate-500 hover:bg-slate-200"
                    }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
              <TopProfilesTable rows={topTab === "views" ? topByViews : topByStreak} mode={topTab} />
            </Section>

            {/* ─── Story → Interest funnel explainer ────────────────────────── */}
            {(overview.storyInterestsSent7d ?? 0) === 0 && (
              <div className="border border-slate-200 bg-slate-50 rounded-2xl p-5">
                <div className="flex items-start gap-3">
                  <FontAwesomeIcon icon={faCircleInfo} className="text-slate-400 text-sm mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-semibold text-slate-700">Story → Interest tracking</p>
                    <p className="text-xs text-slate-500 leading-relaxed mt-1">
                      Interests sent directly from the status viewer are tracked via <code className="bg-slate-100 px-1 rounded text-[10px]">source: 'story_viewer'</code> on the MatchConnection model.
                      This will populate once users start connecting through status views.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default StatusAnalytics;
